/* Copyright 2023 Amazon.com and its affiliates; all rights reserved. This file is Amazon Web Services Content and may not be duplicated or distributed without permission. */

import React, { useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { UserContext } from '../context/userContext';
import { UserInfoContextType } from '../@types/user';
import Chart from './components/common/chart';
import Grid from "@cloudscape-design/components/grid";
import { fieldDefinition } from './components/common/fieldDefinitionx';
import {
  colorChartsStatusNeutral,
  colorChartsStatusHigh,
  colorChartsStatusPositive,
  colorChartsThresholdPositive
} from '@cloudscape-design/design-tokens';
import Select from "@cloudscape-design/components/select";
import { 
  SpaceBetween, 
  BarChart,
  Box, 
  Button, 
  Container, 
  Header, 
  Cards,
  Pagination,
  TextFilter,
  ColumnLayout,
  FormField,
  Multiselect,
  Link as CDLink,
  Modal,
  Checkbox,
  Toggle
} from '@cloudscape-design/components';

//Mock imports only for demo purpose
import LineChart from "@cloudscape-design/components/line-chart";
import PieChart from "@cloudscape-design/components/pie-chart";
import ProgressBar from "@cloudscape-design/components/progress-bar";
import Badge from "@cloudscape-design/components/badge";
import Alert from "@cloudscape-design/components/alert";
import Popover from "@cloudscape-design/components/popover";
import Icon from "@cloudscape-design/components/icon";
import MixedLineBarChart from "@cloudscape-design/components/mixed-line-bar-chart";
import { Link } from 'react-router-dom';
import { getApiUrl } from '../utils/apiConfig';

export default function Home() {
    // Dashboard configuration options
    const dashboardConfig = {
        title: "RFx Qualification Dashboard",
        description: "Overview of qualification metrics and trends"
    };
    
    // Widget visibility configuration
    interface WidgetConfig {
        id: string;
        title: string;
        visible: boolean;
        category?: string;
    }
    
    const [widgetConfigs, setWidgetConfigs] = useState<WidgetConfig[]>([
        // Core widgets
        { id: 'requestTypes', title: 'Request Types', visible: true, category: 'Core Metrics' },
        { id: 'winLoss', title: 'Win/Loss Ratio', visible: true, category: 'Core Metrics' },
        { id: 'industry', title: 'Industry Distribution', visible: true, category: 'Core Metrics' },
        { id: 'buildVsBuy', title: 'Build vs Buy', visible: true, category: 'Core Metrics' },
        { id: 'businessQualification', title: 'Business Qualification Status', visible: true, category: 'Core Metrics' },
        { id: 'solutionsQualification', title: 'Solutions Qualification Status', visible: true, category: 'Core Metrics' },
        { id: 'cloudCustomer', title: 'Cloud Customer Status', visible: true, category: 'Core Metrics' },
        { id: 'partnerDistribution', title: 'Partner Distribution', visible: true, category: 'Core Metrics' },
        { id: 'lostAgainst', title: 'Lost Against Competitors', visible: true, category: 'Core Metrics' },
        { id: 'averageAge', title: 'Average Item Age', visible: true, category: 'Core Metrics' },
        { id: 'qualificationState', title: 'Qualification States', visible: true, category: 'Core Metrics' },
        { id: 'savedTime', title: 'Saved Time by Qualify Out', visible: true, category: 'Core Metrics' },
        { id: 'avgDealSizeByRegion', title: 'Average Deal Size by Region', visible: true, category: 'Financial Metrics' },
        { id: 'avgDealSizeByIndustry', title: 'Average Deal Size by Industry', visible: true, category: 'Financial Metrics' },
        
        // New High Priority Widgets
        { id: 'monthlyWinRate', title: 'Monthly Win Rate Trend', visible: true, category: 'Performance Analytics' },
        { id: 'dealProbability', title: 'Deal Probability Score', visible: true, category: 'Performance Analytics' },
        { id: 'dealsAtRisk', title: 'Deals at Risk', visible: true, category: 'Performance Analytics' },
        { id: 'timeToClose', title: 'Time to Close Analysis', visible: true, category: 'Performance Analytics' },
        
        // Business metrics
        { id: 'pricingApproach', title: 'Consumption Pricing', visible: false, category: 'Business Metrics' },
        { id: 'objective', title: 'Objective (Tactical vs Strategic)', visible: false, category: 'Business Metrics' },
        { id: 'compellingEvent', title: 'Compelling Event', visible: false, category: 'Business Metrics' },
        { id: 'awsSentiment', title: 'AWS Sentiment', visible: false, category: 'Business Metrics' },
        
        // Technical metrics
        { id: 'deliveryModel', title: 'Delivery Model', visible: false, category: 'Technical Metrics' },
        { id: 'opportunityType', title: 'Opportunity Type', visible: false, category: 'Technical Metrics' },
        
        // Table
        { id: 'itemsTable', title: 'Items Table', visible: true, category: 'Tables' }
    ]);
    
    const [configModalVisible, setConfigModalVisible] = useState(false);

    const apiUrl = getApiUrl();
    const path = '/getMyItems';
    const pathAll = '/getItems';
    const [selectedPath, setSelectedPath] = useState(path);
    const [
      selectedOption,
      setSelectedOption
    ] = React.useState<{
      label?: string;
      value?: string;
    }>({
      label: "My Items",
      value: "/getMyItems",
    });
    
    // Filter states
    interface OptionDefinition {
        label: string;
        value: string;
    }
    const [industryFilter, setIndustryFilter] = useState<readonly OptionDefinition[]>([]);
    const [requestTypeFilter, setRequestTypeFilter] = useState<readonly OptionDefinition[]>([]);
    const [outcomeFilter, setOutcomeFilter] = useState<readonly OptionDefinition[]>([]);
    const [buildFilter, setBuildFilter] = useState<readonly OptionDefinition[]>([]);
    const [qualificationStateFilter, setQualificationStateFilter] = useState<readonly OptionDefinition[]>([]);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [filteringText, setFilteringText] = useState('');

    //Interfaces
    interface cloudCustomerDataItem {
      title: string;
      value: number;
      color: string
    }

    //Context Information 
    const { user } = React.useContext(UserContext) as UserInfoContextType;

    //State
    const [chartLoading, setChartLoading] = useState(true)
    //ChartInfo overall RFX
    const [data, setData] = useState([]);
    const [innerMetricValue, setInnerMetricValue] = useState('');
    const [innerMetricValueLost, setInnerMetricValueLost] = useState(0);

    //ChartInfo 
    const [businessData, setBusinessData] = useState([]);
    const [solutionsData, setSolutionsData] = useState([]);
    const [industryData, setIndustryData] = useState([]);
    const [buildData, setBuildData] = useState([]);
    const [cloudCustomerData, setCloudCustomerData] = useState<any[]>([]);
    const [cloudCustomerDataCount, setCloudCustomerDataCount] = useState(0);
    const [winLossCounts, setWinLossCounts] = useState<any[]>([]);
    const [winLossItemCount, setWinLossItemCount] = useState(0);
    const [lostAgainst, setLostAgainst] = useState([]);
    const [partnerSelected, setPartnerSelected] = useState<any[]>([]);
    const [partnerSelectedCount, setPartnerSelectedCount] = useState(0);
    
    // Additional chart data for new widgets
    const [pricingApproachData, setPricingApproachData] = useState<any[]>([]);
    const [objectiveData, setObjectiveData] = useState<any[]>([]);
    const [compellingEventData, setCompellingEventData] = useState<any[]>([]);
    const [awsSentimentData, setAwsSentimentData] = useState<any[]>([]);
    const [deliveryModelData, setDeliveryModelData] = useState<any[]>([]);
    const [opportunityTypeData, setOpportunityTypeData] = useState<any[]>([]);
    const [averageItemAge, setAverageItemAge] = useState<number>(0);
    const [avgDealSizeByRegion, setAvgDealSizeByRegion] = useState<any[]>([]);
    const [avgDealSizeByIndustry, setAvgDealSizeByIndustry] = useState<any[]>([]);
    const [showAllRegions, setShowAllRegions] = useState<boolean>(false);
    const [showAllIndustries, setShowAllIndustries] = useState<boolean>(false);
    const [qualificationStateData, setQualificationStateData] = useState<any[]>([]);
    const [savedTimeByQualifyOut, setSavedTimeByQualifyOut] = useState<number>(0);
    const [rawData, setRawData] = useState<any[]>([]);
    
    // New Performance Analytics Data
    const [monthlyWinRateData, setMonthlyWinRateData] = useState<any[]>([]);
    const [dealProbabilityData, setDealProbabilityData] = useState<any[]>([]);
    const [dealsAtRiskData, setDealsAtRiskData] = useState<any[]>([]);
    const [timeToCloseData, setTimeToCloseData] = useState<any[]>([]);
    const [averageTimeToClose, setAverageTimeToClose] = useState<number>(0);
    const [highRiskDealsCount, setHighRiskDealsCount] = useState<number>(0);
    
    // Items table state
    const [showAllItems, setShowAllItems] = useState<boolean>(false);

    // Apply filters to data
    const applyFilters = () => {
        let filtered = [...rawData];
        
        if (industryFilter.length > 0) {
            filtered = filtered.filter(item => 
                industryFilter.some(filter => filter.value === item.companyIndustry)
            );
        }
        
        if (requestTypeFilter.length > 0) {
            filtered = filtered.filter(item => 
                requestTypeFilter.some(filter => filter.value === item.requestType)
            );
        }
        
        if (outcomeFilter.length > 0) {
            filtered = filtered.filter(item => 
                outcomeFilter.some(filter => filter.value === item.tenderOutcome)
            );
        }
        
        if (buildFilter.length > 0) {
            filtered = filtered.filter(item => 
                buildFilter.some(filter => filter.value === item.buildVsBuy)
            );
        }
        
        if (qualificationStateFilter.length > 0) {
            filtered = filtered.filter(item => 
                item.qualificationState && qualificationStateFilter.some(filter => 
                    filter.value === item.qualificationState.value
                )
            );
        }
        
        if (filteringText) {
            const lowerText = filteringText.toLowerCase();
            filtered = filtered.filter(item => 
                item.companyName?.toLowerCase().includes(lowerText) ||
                item.primaryBusinessContactName?.toLowerCase().includes(lowerText) ||
                item.qualificationState?.value?.toLowerCase().includes(lowerText)
            );
        }
        
        setFilteredData(filtered);
        processChartData(filtered);
    };

    // Process chart data from filtered results
    const processChartData = (dataset: any[]) => {
        const countObject:any = {}
        const countObjectBusinessCompleted:any = {};
        const countObjectSolutionsCompleted:any = {};
        const countObjectCustomerIndustry:any = {};
        const countObjectBuild:any = {};
        const countObjectCloudCustomer:any = {};
        const countObjectWinLoss:any = {};
        const countObjectLostAgainst:any = {};
        const countObjectPartnerSelected:any = {};
        
        if(dataset.length > 0) {
            dataset.forEach((item: any) => {
                Object.keys(item).forEach(key => {
                    if (key === "requestType"){
                        countObject[item[key]] = countObject[item[key]] + 1 || 1
                    }
                    if (key === "postQualificationBusiness"){
                        countObjectBusinessCompleted[item[key]] = countObjectBusinessCompleted[item[key]] + 1 || 1
                    }
                    if (key === "postQualificationSolution"){
                        countObjectSolutionsCompleted[item[key]] = countObjectSolutionsCompleted[item[key]] + 1 || 1
                    }
                    if (key === "companyIndustry"){
                        countObjectCustomerIndustry[item[key]] = countObjectCustomerIndustry[item[key]] + 1 || 1
                    }
                    if (key === 'buildVsBuy'){
                        countObjectBuild[item[key]] = countObjectBuild[item[key]] + 1 || 1
                    }
                    if (key === 'cloudCustomer'){
                        countObjectCloudCustomer[item[key]] = countObjectCloudCustomer[item[key]] + 1 || 1
                    }
                    if (key === 'tenderOutcome'){
                        countObjectWinLoss[item[key]] = countObjectWinLoss[item[key]] + 1 || 1
                    } 
                    if (key === 'solutionChosen' && item['solutionChosen'] != '' && item['tenderOutcome'] === 'Loss'){
                        countObjectLostAgainst[item[key]] = countObjectLostAgainst[item[key]] + 1 || 1
;                   }
                    if (key === 'partner') {
                      if(item[key] === '') {
                        countObjectPartnerSelected['No Partner'] = countObjectPartnerSelected['No Partner'] + 1 || 1
                      } else {
                        countObjectPartnerSelected[item[key]] = countObjectPartnerSelected[item[key]] + 1 || 1
                      }
                    }
                })
            })
            const dataSet:any = [];
            const dataSetBusinessCompleted:any = [];
            const dataSetSolutionsCompleted:any = [];
            const dataSetCustomerIndustry:any = [];
            const dataSetBuild:any = [];
            const dataSetCloudCustomer:any = [];
            const dataSetTenderOutcome:any = [];
            const dataSetLostAgainst:any = [];
            const dataSetPartnerSelected:any = [];
            let color;
            Object.keys(countObject).forEach(key => {
                dataSet.push({
                    title: key,
                    value: countObject[key]
                })
            })
            Object.keys(countObjectBusinessCompleted).forEach(key => {
              key === 'Completed' ? color=colorChartsStatusPositive : color=colorChartsStatusHigh
                dataSetBusinessCompleted.push({
                    title: key,
                    value: countObjectBusinessCompleted[key],
                    color: color
                })
            })
            Object.keys(countObjectSolutionsCompleted).forEach(key => {
              key === 'Completed' ? color=colorChartsStatusPositive : color=colorChartsStatusHigh
                dataSetSolutionsCompleted.push({
                    title: key,
                    value: countObjectSolutionsCompleted[key],
                    color: color
                })
            })
            Object.keys(countObjectCustomerIndustry).forEach(key => {
                dataSetCustomerIndustry.push({
                    title: key,
                    value: countObjectCustomerIndustry[key]
                })
            })
            Object.keys(countObjectBuild).forEach(key => {
              key === 'Build' ? color=colorChartsStatusPositive : color=colorChartsStatusHigh
                dataSetBuild.push({
                    title: key,
                    value: countObjectBuild[key],
                    color: color
                })
            })
            Object.keys(countObjectCloudCustomer).forEach(key => {
              key === 'Yes' ? color=colorChartsStatusPositive : color=colorChartsStatusHigh
                dataSetCloudCustomer.push({
                    title: key,
                    value: countObjectCloudCustomer[key],
                    color: color
                })
            })
            setCloudCustomerDataCount(Object.keys(countObjectCloudCustomer).reduce((sum, key) => sum + countObjectCloudCustomer[key], 0))
            
            Object.keys(countObjectWinLoss).forEach(key => {
                // case statement which checks if key is Win, Loss or Pending
                color=colorChartsStatusNeutral
                if(key === 'Win') {
                    color=colorChartsStatusPositive
                } else if(key === 'Loss') {
                    color=colorChartsStatusHigh
                } else {
                    color=colorChartsStatusNeutral
                }
              
                dataSetTenderOutcome.push({
                    title: key,
                    value: countObjectWinLoss[key],
                    color: color
                })
            })
            setWinLossItemCount(Object.keys(countObjectWinLoss).reduce((sum, key) => sum + countObjectWinLoss[key], 0))
            
            Object.keys(countObjectLostAgainst).forEach(key => {
                dataSetLostAgainst.push({
                    title: key,
                    value: countObjectLostAgainst[key]
                })
            })
            
            Object.keys(countObjectPartnerSelected).forEach(key => {
                dataSetPartnerSelected.push({
                    title: key,
                    value: countObjectPartnerSelected[key]
                })
            })
            setPartnerSelectedCount(Object.keys(countObjectPartnerSelected).reduce((sum, key) => sum + countObjectPartnerSelected[key], 0))
            
            // Set all the chart data
            setData(dataSet);
            setBusinessData(dataSetBusinessCompleted);
            setSolutionsData(dataSetSolutionsCompleted);
            setIndustryData(dataSetCustomerIndustry);
            setBuildData(dataSetBuild);
            setCloudCustomerData(dataSetCloudCustomer);
            setWinLossCounts(dataSetTenderOutcome);
            setLostAgainst(dataSetLostAgainst);
            setPartnerSelected(dataSetPartnerSelected);
            
            // Process additional metrics
            const processMetric = (field: string, setter: Function) => {
                const countObj: any = {};
                dataset.forEach((item: any) => {
                    if (item[field]) {
                        countObj[item[field]] = countObj[item[field]] + 1 || 1;
                    }
                });
                const dataSet = Object.keys(countObj).map(key => ({
                    title: key,
                    value: countObj[key],
                    color: key === 'Yes' ? colorChartsStatusPositive : 
                           key === 'No' ? colorChartsStatusHigh : colorChartsStatusNeutral
                }));
                setter(dataSet);
            };
            
            // Process qualification state data
            const processQualificationStates = () => {
                const qualificationStates: any = {};
                dataset.forEach(item => {
                    if (item.qualificationState && item.qualificationState.label) {
                        const state = item.qualificationState.label;
                        qualificationStates[state] = qualificationStates[state] + 1 || 1;
                    } else {
                        qualificationStates['Not-set'] = qualificationStates['Not-set'] + 1 || 1;
                    }
                });
                
                const stateData = Object.keys(qualificationStates).map(state => {
                    let color = colorChartsStatusNeutral;
                    if (state.includes('Qualified In')) {
                        color = colorChartsStatusPositive;
                    } else if (state === 'Qualified Out') {
                        color = colorChartsStatusHigh;
                    }
                    
                    return {
                        title: state,
                        value: qualificationStates[state],
                        color: color
                    };
                });
                
                setQualificationStateData(stateData);
            };
            
            // Calculate saved time by qualify out
            const calculateSavedTime = () => {
                let totalSavedHours = 0;
                
                dataset.forEach(item => {
                    if (item.qualificationState && 
                        item.qualificationState.value === 'Qualified Out' && 
                        item.specialistEffortDays && 
                        !isNaN(parseFloat(item.specialistEffortDays))) {
                        
                        totalSavedHours += parseFloat(item.specialistEffortDays);
                    }
                });
                
                setSavedTimeByQualifyOut(totalSavedHours);
            };
            
            // Calculate average item age
            const calculateAverageAge = () => {
                if (dataset.length === 0) {
                    setAverageItemAge(0);
                    return;
                }
                
                const now = Date.now();
                let totalAge = 0;
                let itemsWithCreatedAt = 0;
                
                dataset.forEach(item => {
                    if (item.createdAt) {
                        const createdTimestamp = parseInt(item.createdAt);
                        if (!isNaN(createdTimestamp)) {
                            const ageInDays = Math.floor((now - createdTimestamp) / (1000 * 60 * 60 * 24));
                            totalAge += ageInDays;
                            itemsWithCreatedAt++;
                        }
                    }
                });
                
                const avgAge = itemsWithCreatedAt > 0 ? Math.round(totalAge / itemsWithCreatedAt) : 0;
                setAverageItemAge(avgAge);
            };
            
            // Calculate average deal size by region
            const calculateAvgDealSizeByRegion = () => {
                if (dataset.length === 0) {
                    setAvgDealSizeByRegion([]);
                    return;
                }
                
                const regionTotals: {[key: string]: {sum: number, count: number}} = {};
                
                dataset.forEach(item => {
                    if (item.regionCountry && item.expectedARR && !isNaN(parseFloat(item.expectedARR))) {
                        const region = item.regionCountry;
                        const arr = parseFloat(item.expectedARR);
                        
                        if (!regionTotals[region]) {
                            regionTotals[region] = { sum: 0, count: 0 };
                        }
                        
                        regionTotals[region].sum += arr;
                        regionTotals[region].count += 1;
                    }
                });
                
                const avgByRegion = Object.keys(regionTotals).map(region => ({
                    title: region,
                    value: regionTotals[region].count > 0 ? 
                        parseFloat((regionTotals[region].sum / regionTotals[region].count).toFixed(2)) : 0
                }))
                .sort((a, b) => b.value - a.value); // Sort by average ARR descending
                
                setAvgDealSizeByRegion(avgByRegion);
            };
            
            // Calculate average deal size by industry
            const calculateAvgDealSizeByIndustry = () => {
                if (dataset.length === 0) {
                    setAvgDealSizeByIndustry([]);
                    return;
                }
                
                const industryTotals: {[key: string]: {sum: number, count: number, totalValue: number}} = {};
                
                dataset.forEach(item => {
                    if (item.companyIndustry && item.expectedARR && !isNaN(parseFloat(item.expectedARR))) {
                        const industry = item.companyIndustry;
                        const arr = parseFloat(item.expectedARR);
                        
                        if (!industryTotals[industry]) {
                            industryTotals[industry] = { sum: 0, count: 0, totalValue: 0 };
                        }
                        
                        industryTotals[industry].sum += arr;
                        industryTotals[industry].count += 1;
                        industryTotals[industry].totalValue += arr;
                    }
                });
                
                const avgByIndustry = Object.keys(industryTotals).map(industry => ({
                    title: industry,
                    value: industryTotals[industry].count > 0 ? 
                        parseFloat((industryTotals[industry].sum / industryTotals[industry].count).toFixed(2)) : 0,
                    totalValue: industryTotals[industry].totalValue
                }))
                .sort((a, b) => b.value - a.value); // Sort by average ARR descending
                
                setAvgDealSizeByIndustry(avgByIndustry);
            };
            
            processMetric('pricingApproach', setPricingApproachData);
            processMetric('objective', setObjectiveData);
            processMetric('compellingEvent', setCompellingEventData);
            processMetric('awsSentiment', setAwsSentimentData);
            processMetric('deliveryModel', setDeliveryModelData);
            processMetric('opportunityType', setOpportunityTypeData);
            
            // Calculate metrics for filtered items
            calculateAverageAge();
            calculateAvgDealSizeByRegion();
            calculateAvgDealSizeByIndustry();
            processQualificationStates();
            calculateSavedTime();
            
            // Calculate lost items
            if(countObjectWinLoss['loss']) {
                setInnerMetricValueLost(countObjectWinLoss['loss']);
            }
        }
        
        // New Performance Analytics Functions
        const calculateMonthlyWinRate = () => {
            const monthlyData: {[key: string]: {wins: number, losses: number, total: number}} = {};
            
            dataset.forEach(item => {
                if (item.createdAt && item.tenderOutcome && item.tenderOutcome !== 'Pending') {
                    const date = new Date(parseInt(item.createdAt));
                    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                    
                    if (!monthlyData[monthKey]) {
                        monthlyData[monthKey] = { wins: 0, losses: 0, total: 0 };
                    }
                    
                    monthlyData[monthKey].total += 1;
                    if (item.tenderOutcome === 'Win') {
                        monthlyData[monthKey].wins += 1;
                    } else if (item.tenderOutcome === 'Loss') {
                        monthlyData[monthKey].losses += 1;
                    }
                }
            });
            
            const sortedMonths = Object.keys(monthlyData).sort();
            
            // Prepare data for mixed chart (line + bars)
            const chartData = sortedMonths.map(month => {
                const data = monthlyData[month];
                const winRate = data.total > 0 ? Math.round((data.wins / data.total) * 100) : 0;
                
                return {
                    x: month,
                    winRate: winRate,
                    wins: data.wins,
                    losses: data.losses,
                    total: data.total
                };
            });
            
            setMonthlyWinRateData(chartData);
        };
        
        const calculateDealProbability = () => {
            const probabilityBuckets: {[key: string]: number} = {
                'High (80-100%)': 0,
                'Medium-High (60-79%)': 0,
                'Medium (40-59%)': 0,
                'Low (20-39%)': 0,
                'Very Low (0-19%)': 0
            };
            
            dataset.forEach(item => {
                if (item.tenderOutcome === 'Pending') {
                    let score = 0;
                    
                    // Business qualification completed
                    if (item.postQualificationBusiness === 'Completed') score += 20;
                    
                    // Solutions qualification completed
                    if (item.postQualificationSolution === 'Completed') score += 20;
                    
                    // Existing cloud customer
                    if (item.cloudCustomer === 'Yes') score += 15;
                    
                    // AWS sentiment positive
                    if (item.awsSentiment === 'Yes') score += 15;
                    
                    // Compelling event exists
                    if (item.compellingEvent === 'Yes') score += 10;
                    
                    // Strategic vs tactical
                    if (item.objective === 'Strategic') score += 10;
                    
                    // Partner involvement
                    if (item.partner && item.partner !== '') score += 10;
                    
                    // Categorize by score
                    if (score >= 80) probabilityBuckets['High (80-100%)']++;
                    else if (score >= 60) probabilityBuckets['Medium-High (60-79%)']++;
                    else if (score >= 40) probabilityBuckets['Medium (40-59%)']++;
                    else if (score >= 20) probabilityBuckets['Low (20-39%)']++;
                    else probabilityBuckets['Very Low (0-19%)']++;
                }
            });
            
            const probabilityData = Object.keys(probabilityBuckets).map(bucket => ({
                title: bucket,
                value: probabilityBuckets[bucket],
                color: bucket.includes('High') ? colorChartsStatusPositive :
                       bucket.includes('Medium') ? colorChartsThresholdPositive :
                       bucket.includes('Low') ? colorChartsStatusHigh : colorChartsStatusNeutral
            }));
            
            setDealProbabilityData(probabilityData);
        };
        
        const identifyDealsAtRisk = () => {
            const now = Date.now();
            const riskDeals: any[] = [];
            let riskCount = 0;
            
            dataset.forEach(item => {
                if (item.tenderOutcome === 'Pending' && item.createdAt) {
                    const createdDate = parseInt(item.createdAt);
                    const daysSinceCreated = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
                    
                    let riskScore = 0;
                    let riskFactors: string[] = [];
                    
                    // Age-based risk
                    if (daysSinceCreated > 90) {
                        riskScore += 30;
                        riskFactors.push('Stale opportunity (>90 days)');
                    } else if (daysSinceCreated > 60) {
                        riskScore += 15;
                        riskFactors.push('Aging opportunity (>60 days)');
                    }
                    
                    // Qualification risk
                    if (item.postQualificationBusiness !== 'Completed') {
                        riskScore += 20;
                        riskFactors.push('Business qualification incomplete');
                    }
                    
                    if (item.postQualificationSolution !== 'Completed') {
                        riskScore += 20;
                        riskFactors.push('Solutions qualification incomplete');
                    }
                    
                    // Customer risk factors
                    if (item.cloudCustomer === 'No') {
                        riskScore += 15;
                        riskFactors.push('Not existing cloud customer');
                    }
                    
                    if (item.awsSentiment === 'No') {
                        riskScore += 25;
                        riskFactors.push('Negative AWS sentiment');
                    }
                    
                    if (riskScore >= 40) {
                        riskCount++;
                        riskDeals.push({
                            ...item,
                            riskScore,
                            riskFactors,
                            daysSinceCreated
                        });
                    }
                }
            });
            
            setHighRiskDealsCount(riskCount);
            setDealsAtRiskData(riskDeals.sort((a, b) => b.riskScore - a.riskScore).slice(0, 10));
        };
        
        const calculateTimeToClose = () => {
            const closedDeals = dataset.filter(item => 
                item.tenderOutcome && item.tenderOutcome !== 'Pending' && 
                item.createdAt && item.updatedAt
            );
            
            if (closedDeals.length === 0) {
                setTimeToCloseData([]);
                setAverageTimeToClose(0);
                return;
            }
            
            const timeBuckets: {[key: string]: number} = {
                '0-30 days': 0,
                '31-60 days': 0,
                '61-90 days': 0,
                '91-180 days': 0,
                '180+ days': 0
            };
            
            let totalDays = 0;
            
            closedDeals.forEach(item => {
                const created = parseInt(item.createdAt);
                const updated = parseInt(item.updatedAt);
                const daysToClose = Math.floor((updated - created) / (1000 * 60 * 60 * 24));
                
                totalDays += daysToClose;
                
                if (daysToClose <= 30) timeBuckets['0-30 days']++;
                else if (daysToClose <= 60) timeBuckets['31-60 days']++;
                else if (daysToClose <= 90) timeBuckets['61-90 days']++;
                else if (daysToClose <= 180) timeBuckets['91-180 days']++;
                else timeBuckets['180+ days']++;
            });
            
            const timeData = Object.keys(timeBuckets).map(bucket => ({
                title: bucket,
                value: timeBuckets[bucket],
                color: bucket === '0-30 days' ? colorChartsStatusPositive :
                       bucket === '31-60 days' ? colorChartsThresholdPositive :
                       colorChartsStatusNeutral
            }));
            
            setTimeToCloseData(timeData);
            setAverageTimeToClose(Math.round(totalDays / closedDeals.length));
        };
        
        // Call the new analytics functions if we have data
        if(dataset.length > 0) {
            calculateMonthlyWinRate();
            calculateDealProbability();
            identifyDealsAtRisk();
            calculateTimeToClose();
        }
    };

    useEffect(() => {
        const getOwnItems = async () => {
            const token = (await fetchAuthSession()).tokens?.idToken?.toString();
            const body = {
                user: user.username
            };
            const response = await fetch(`${apiUrl}${selectedPath}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });
            const data = await response.json();
            setInnerMetricValue(String(data.dataset.length));
            setRawData(data.dataset);
            setFilteredData(data.dataset);
            return data;
        }

        if (user.username !== 'post 1') {
            setChartLoading(true);
            setInnerMetricValueLost(0);
            getOwnItems().then((data) => {
                setChartLoading(false);
                processChartData(data.dataset);
            });
        }
    }, [selectedPath, user.username]);

    // Apply filters when filter states change
    useEffect(() => {
        applyFilters();
    }, [industryFilter, requestTypeFilter, outcomeFilter, buildFilter, qualificationStateFilter, filteringText]);
    
    // Save widget configuration to localStorage
    useEffect(() => {
        localStorage.setItem('dashboardWidgetConfig', JSON.stringify(widgetConfigs));
    }, [widgetConfigs]);
    
    // Load widget configuration from localStorage
    useEffect(() => {
        const savedConfig = localStorage.getItem('dashboardWidgetConfig');
        if (savedConfig) {
            try {
                setWidgetConfigs(JSON.parse(savedConfig));
            } catch (e) {
                console.error('Failed to parse saved dashboard configuration');
            }
        }
    }, []);

    // Extract unique values for filter options
    const getFilterOptions = (field: string) => {
        if (!rawData.length) return [];
        
        const uniqueValues = [...new Set(rawData.map(item => item[field]))];
        return uniqueValues.filter(Boolean).map(value => ({ label: value, value }));
    };

    // Handle view toggle between My Items and All Items
    const handleViewToggle = (option: any) => {
        setSelectedOption(option);
        setSelectedPath(option.value);
    };
    
    // Handle widget visibility toggle
    const handleWidgetToggle = (id: string, checked: boolean) => {
        setWidgetConfigs(prev => 
            prev.map(widget => 
                widget.id === id ? { ...widget, visible: checked } : widget
            )
        );
    };
    
    // Check if a widget should be visible
    const isWidgetVisible = (id: string) => {
        const widget = widgetConfigs.find(w => w.id === id);
        return widget ? widget.visible : true;
    };

    return (
        <SpaceBetween size="l">
            <Container
                header={
                    <Header
                        variant="h1"
                        description={dashboardConfig.description}
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button 
                                    iconName="settings"
                                    onClick={() => setConfigModalVisible(true)}
                                >
                                    Configure Dashboard
                                </Button>
                                <Select
                                    selectedOption={selectedOption}
                                    onChange={({ detail }) => handleViewToggle(detail.selectedOption)}
                                    options={[
                                        { label: "My Items", value: path },
                                        { label: "All Items", value: pathAll }
                                    ]}
                                />
                            </SpaceBetween>
                        }
                    >
                        {dashboardConfig.title}
                    </Header>
                }
            >
                <SpaceBetween size="l">
                    {/* Filter section */}
                    <Container header={<Header variant="h2">Filters</Header>}>
                        <ColumnLayout columns={2} variant="text-grid">
                            <SpaceBetween size="m">
                                <FormField label="Search">
                                    <TextFilter
                                        filteringText={filteringText}
                                        onChange={({ detail }) => setFilteringText(detail.filteringText)}
                                        countText={`${filteredData.length} matches`}
                                        filteringPlaceholder="Search by company, contact, or status"
                                    />
                                </FormField>
                                <FormField label="Industry">
                                    <Multiselect
                                        selectedOptions={industryFilter}
                                        onChange={({ detail }) => setIndustryFilter(detail.selectedOptions as readonly any[])}
                                        options={getFilterOptions('companyIndustry')}
                                        filteringPlaceholder="Filter by industry"
                                    />
                                </FormField>
                            </SpaceBetween>
                            <SpaceBetween size="m">
                                <FormField label="Request Type">
                                    <Multiselect
                                        selectedOptions={requestTypeFilter}
                                        onChange={({ detail }) => setRequestTypeFilter(detail.selectedOptions as readonly any[])}
                                        options={getFilterOptions('requestType')}
                                        filteringPlaceholder="Filter by request type"
                                    />
                                </FormField>
                                <FormField label="Outcome">
                                    <Multiselect
                                        selectedOptions={outcomeFilter}
                                        onChange={({ detail }) => setOutcomeFilter(detail.selectedOptions as readonly any[])}
                                        options={getFilterOptions('tenderOutcome')}
                                        filteringPlaceholder="Filter by outcome"
                                    />
                                </FormField>
                                <FormField label="Qualification State">
                                    <Multiselect
                                        selectedOptions={qualificationStateFilter}
                                        onChange={({ detail }) => setQualificationStateFilter(detail.selectedOptions as readonly any[])}
                                        options={[
                                            { label: 'Qualified In - AWS led', value: 'Qualified In - AWS led' },
                                            { label: 'Qualified In - Partner led', value: 'Qualified In - Partner led' },
                                            { label: 'Qualified In - Disruptive bid', value: 'Qualified In - Disruptive bid' },
                                            { label: 'Qualified Out', value: 'Qualified Out' },
                                            { label: 'Not-set', value: 'Not-set' }
                                        ]}
                                        filteringPlaceholder="Filter by qualification state"
                                    />
                                </FormField>
                            </SpaceBetween>
                        </ColumnLayout>
                    </Container>

                    {/* Average Age Widget - Full Width */}
                    {isWidgetVisible('averageAge') && (
                        <Container>
                            <Grid
                                gridDefinition={[
                                    { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 12, l: 12, xl: 12 } }
                                ]}
                            >
                                <Box textAlign="center" padding={{ top: 'l', bottom: 'l' }}>
                                    <Box variant="h2" padding={{ bottom: 's' }}>Average Item Age</Box>
                                    <Box variant="h1" color="text-status-info" fontSize="display-l">
                                        {averageItemAge} days
                                    </Box>
                                    <Box variant="p" color="text-body-secondary">
                                        Based on {filteredData.length} items
                                    </Box>
                                </Box>
                            </Grid>
                        </Container>
                    )}
                    
                    {/* Saved Time Widget */}
                    {isWidgetVisible('savedTime') && (
                        <Container>
                            <Grid
                                gridDefinition={[
                                    { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 12, l: 12, xl: 12 } }
                                ]}
                            >
                                <Box textAlign="center" padding={{ top: 'l', bottom: 'l' }}>
                                    <Box variant="h2" padding={{ bottom: 's' }}>Saved Time by Qualify Out</Box>
                                    <Box variant="h1" color="text-status-success" fontSize="display-l">
                                        {savedTimeByQualifyOut} hours
                                    </Box>
                                    <Box variant="p" color="text-body-secondary">
                                        Total specialist effort saved
                                    </Box>
                                </Box>
                            </Grid>
                        </Container>
                    )}
                    
                    {/* Qualification States */}
                    {isWidgetVisible('qualificationState') && (
                        <Container header={<Header variant="h2">Qualification States</Header>}>
                            <Chart
                                data={qualificationStateData}
                                chartLabel="Qualification States"
                                chartDescription="Distribution of qualification states"
                                innerMetricDescription="Total"
                                innerMetricValue={String(qualificationStateData.reduce((sum, item) => sum + item.value, 0))}
                                size="large"
                                variant="pie"
                                statusType={chartLoading}
                            />
                        </Container>
                    )}
                    
                    {/* Summary metrics */}
                    <Grid
                        gridDefinition={[
                            { colspan: { default: 12, xxs: 6, xs: 6, s: 6, m: 3, l: 3, xl: 3 } },
                            { colspan: { default: 12, xxs: 6, xs: 6, s: 6, m: 3, l: 3, xl: 3 } },
                            { colspan: { default: 12, xxs: 6, xs: 6, s: 6, m: 3, l: 3, xl: 3 } },
                            { colspan: { default: 12, xxs: 6, xs: 6, s: 6, m: 3, l: 3, xl: 3 } }
                        ]}
                    >
                        {isWidgetVisible('requestTypes') && (
                            <Chart
                                data={data}
                                chartLabel="Request Types"
                                chartDescription="Distribution of request types"
                                innerMetricDescription="Total"
                                innerMetricValue={String(winLossItemCount)}
                                size="medium"
                                variant="donut"
                                statusType={chartLoading}
                            />
                        )}
                        {isWidgetVisible('winLoss') && (
                            <Chart
                                data={winLossCounts}
                                chartLabel="Win/Loss Ratio"
                                chartDescription="Win vs Loss distribution"
                                innerMetricDescription="Total"
                                innerMetricValue={String(winLossItemCount)}
                                size="medium"
                                variant="donut"
                                statusType={chartLoading}
                            />
                        )}
                        {isWidgetVisible('industry') && (
                            <Chart
                                data={industryData}
                                chartLabel="Industry Distribution"
                                chartDescription="Distribution by industry"
                                innerMetricDescription="Total"
                                innerMetricValue={String(winLossItemCount)}
                                size="medium"
                                variant="donut"
                                statusType={chartLoading}
                            />
                        )}
                        {isWidgetVisible('buildVsBuy') && (
                            <Chart
                                data={buildData}
                                chartLabel="Build vs Buy"
                                chartDescription="Build vs Buy distribution"
                                innerMetricDescription="Total"
                                innerMetricValue={String(winLossItemCount)}
                                size="medium"
                                variant="donut"
                                statusType={chartLoading}
                            />
                        )}
                    </Grid>

                    {/* Detailed metrics */}
                    {(isWidgetVisible('businessQualification') || isWidgetVisible('solutionsQualification')) && (
                        <Grid
                            gridDefinition={[
                                { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 6, l: 6, xl: 6 } },
                                { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 6, l: 6, xl: 6 } }
                            ]}
                        >
                            {isWidgetVisible('businessQualification') && (
                                <Chart
                                    data={businessData}
                                    chartLabel="Business Qualification Status"
                                    chartDescription="Status of business qualification checklist"
                                    innerMetricDescription="Total"
                                    innerMetricValue={innerMetricValue}
                                    size="large"
                                    variant="pie"
                                    statusType={chartLoading}
                                />
                            )}
                            {isWidgetVisible('solutionsQualification') && (
                                <Chart
                                    data={solutionsData}
                                    chartLabel="Solutions Qualification Status"
                                    chartDescription="Status of solutions qualification checklist"
                                    innerMetricDescription="Total"
                                    innerMetricValue={innerMetricValue}
                                    size="large"
                                    variant="pie"
                                    statusType={chartLoading}
                                />
                            )}
                        </Grid>
                    )}

                    {/* Additional metrics */}
                    {(isWidgetVisible('cloudCustomer') || isWidgetVisible('partnerDistribution')) && (
                        <Grid
                            gridDefinition={[
                                { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 6, l: 6, xl: 6 } },
                                { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 6, l: 6, xl: 6 } }
                            ]}
                        >
                            {isWidgetVisible('cloudCustomer') && (
                                <Chart
                                    data={cloudCustomerData}
                                    chartLabel="Cloud Customer Status"
                                    chartDescription="Existing cloud customer status"
                                    innerMetricDescription="Total"
                                    innerMetricValue={String(cloudCustomerDataCount)}
                                    size="large"
                                    variant="pie"
                                    statusType={chartLoading}
                                />
                            )}
                            {isWidgetVisible('partnerDistribution') && (
                                <Chart
                                    data={partnerSelected}
                                    chartLabel="Partner Distribution"
                                    chartDescription="Distribution by partner"
                                    innerMetricDescription="Total"
                                    innerMetricValue={String(partnerSelectedCount)}
                                    size="large"
                                    variant="pie"
                                    statusType={chartLoading}
                                />
                            )}
                        </Grid>
                    )}
                    
                    {/* Business metrics */}
                    {(isWidgetVisible('pricingApproach') || isWidgetVisible('objective') || 
                      isWidgetVisible('compellingEvent') || isWidgetVisible('awsSentiment')) && (
                        <Container header={<Header variant="h2">Business Metrics</Header>}>
                            <Grid
                                gridDefinition={[
                                    { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 6, l: 6, xl: 6 } },
                                    { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 6, l: 6, xl: 6 } },
                                    { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 6, l: 6, xl: 6 } },
                                    { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 6, l: 6, xl: 6 } }
                                ]}
                            >
                                {isWidgetVisible('pricingApproach') && (
                                    <Chart
                                        data={pricingApproachData}
                                        chartLabel="Consumption Pricing"
                                        chartDescription="Is the customer bought into our consumption pricing approach?"
                                        innerMetricDescription="Total"
                                        innerMetricValue={innerMetricValue}
                                        size="medium"
                                        variant="donut"
                                        statusType={chartLoading}
                                    />
                                )}
                                {isWidgetVisible('objective') && (
                                    <Chart
                                        data={objectiveData}
                                        chartLabel="Objective"
                                        chartDescription="Tactical optimization vs strategic transformation"
                                        innerMetricDescription="Total"
                                        innerMetricValue={innerMetricValue}
                                        size="medium"
                                        variant="donut"
                                        statusType={chartLoading}
                                    />
                                )}
                                {isWidgetVisible('compellingEvent') && (
                                    <Chart
                                        data={compellingEventData}
                                        chartLabel="Compelling Event"
                                        chartDescription="Has their current solution met its vendor's end of life or is there a major compelling event?"
                                        innerMetricDescription="Total"
                                        innerMetricValue={innerMetricValue}
                                        size="medium"
                                        variant="donut"
                                        statusType={chartLoading}
                                    />
                                )}
                                {isWidgetVisible('awsSentiment') && (
                                    <Chart
                                        data={awsSentimentData}
                                        chartLabel="AWS Sentiment"
                                        chartDescription="Is the decision maker bought into AWS?"
                                        innerMetricDescription="Total"
                                        innerMetricValue={innerMetricValue}
                                        size="medium"
                                        variant="donut"
                                        statusType={chartLoading}
                                    />
                                )}
                            </Grid>
                        </Container>
                    )}
                    
                    {/* Technical metrics */}
                    {(isWidgetVisible('deliveryModel') || isWidgetVisible('opportunityType')) && (
                        <Container header={<Header variant="h2">Technical Metrics</Header>}>
                            <Grid
                                gridDefinition={[
                                    { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 6, l: 6, xl: 6 } },
                                    { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 6, l: 6, xl: 6 } }
                                ]}
                            >
                                {isWidgetVisible('deliveryModel') && (
                                    <Chart
                                        data={deliveryModelData}
                                        chartLabel="Delivery Model"
                                        chartDescription="Do we understand the delivery model that serves the customer best (Partner or Proserve)"
                                        innerMetricDescription="Total"
                                        innerMetricValue={innerMetricValue}
                                        size="large"
                                        variant="pie"
                                        statusType={chartLoading}
                                    />
                                )}
                                {isWidgetVisible('opportunityType') && (
                                    <Chart
                                        data={opportunityTypeData}
                                        chartLabel="Opportunity Type"
                                        chartDescription="What is the mix of digital and voice channels within this opportunity"
                                        innerMetricDescription="Total"
                                        innerMetricValue={innerMetricValue}
                                        size="large"
                                        variant="pie"
                                        statusType={chartLoading}
                                    />
                                )}
                            </Grid>
                        </Container>
                    )}
                    
                    {/* Financial metrics */}
                    {(isWidgetVisible('avgDealSizeByRegion') || isWidgetVisible('avgDealSizeByIndustry')) && (
                        <Container header={<Header variant="h2">Financial Metrics</Header>}>
                            <Grid
                                gridDefinition={[
                                    { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 6, l: 6, xl: 6 } },
                                    { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 6, l: 6, xl: 6 } }
                                ]}
                            >
                                {isWidgetVisible('avgDealSizeByRegion') && (
                                    <Container header={<Header variant="h3">Average Deal Size by Region (ARR in $M)</Header>}>
                                        {avgDealSizeByRegion.length > 0 ? (
                                            <SpaceBetween size="s">
                                                <BarChart
                                                    series={[
                                                        {
                                                            title: "Average ARR ($M)",
                                                            type: "bar",
                                                            data: avgDealSizeByRegion
                                                                .slice(0, showAllRegions ? avgDealSizeByRegion.length : 5)
                                                                .map(item => ({ x: item.title, y: item.value }))
                                                        }
                                                    ]}
                                                    ariaLabel="Average deal size by region"
                                                    height={300}
                                                    hideFilter
                                                    hideLegend
                                                    xScaleType="categorical"
                                                    yTitle="ARR in $M"
                                                    xTitle="Region"
                                                    statusType={chartLoading ? "loading" : "finished"}
                                                    empty={
                                                        <Box textAlign="center" color="inherit">
                                                            <b>No data available</b>
                                                        </Box>
                                                    }
                                                />
                                                {avgDealSizeByRegion.length > 5 && (
                                                    <Box textAlign="center">
                                                        <Button 
                                                            onClick={() => setShowAllRegions(!showAllRegions)}
                                                            variant="link"
                                                        >
                                                            {showAllRegions ? "Show top 5" : `Show all (${avgDealSizeByRegion.length})`}
                                                        </Button>
                                                    </Box>
                                                )}
                                            </SpaceBetween>
                                        ) : (
                                            <Box textAlign="center" padding="l">No data available</Box>
                                        )}
                                    </Container>
                                )}
                                {isWidgetVisible('avgDealSizeByIndustry') && (
                                    <Container header={<Header variant="h3">Average Deal Size by Industry (ARR in $M)</Header>}>
                                        {avgDealSizeByIndustry.length > 0 ? (
                                            <SpaceBetween size="s">
                                                <BarChart
                                                    series={[
                                                        {
                                                            title: "Average ARR ($M)",
                                                            type: "bar",
                                                            data: avgDealSizeByIndustry
                                                                .slice(0, showAllIndustries ? avgDealSizeByIndustry.length : 5)
                                                                .map(item => ({ x: item.title, y: item.value }))
                                                        }
                                                    ]}
                                                    ariaLabel="Average deal size by industry"
                                                    height={300}
                                                    hideFilter
                                                    hideLegend
                                                    xScaleType="categorical"
                                                    yTitle="ARR in $M"
                                                    xTitle="Industry"
                                                    statusType={chartLoading ? "loading" : "finished"}
                                                    empty={
                                                        <Box textAlign="center" color="inherit">
                                                            <b>No data available</b>
                                                        </Box>
                                                    }
                                                />
                                                {avgDealSizeByIndustry.length > 5 && (
                                                    <Box textAlign="center">
                                                        <Button 
                                                            onClick={() => setShowAllIndustries(!showAllIndustries)}
                                                            variant="link"
                                                        >
                                                            {showAllIndustries ? "Show top 5" : `Show all (${avgDealSizeByIndustry.length})`}
                                                        </Button>
                                                    </Box>
                                                )}
                                            </SpaceBetween>
                                        ) : (
                                            <Box textAlign="center" padding="l">No data available</Box>
                                        )}
                                    </Container>
                                )}
                            </Grid>
                        </Container>
                    )}

                    {/* Lost against competitors */}
                    {isWidgetVisible('lostAgainst') && lostAgainst.length > 0 && (
                        <Container header={<Header variant="h2">Lost Against</Header>}>
                            <Chart
                                data={lostAgainst}
                                chartLabel="Lost Against Competitors"
                                chartDescription="Distribution of losses by competitor"
                                innerMetricDescription="Total Losses"
                                innerMetricValue={String(innerMetricValueLost)}
                                size="large"
                                variant="pie"
                                statusType={chartLoading}
                            />
                        </Container>
                    )}
                    
                    {/* Performance Analytics Section */}
                    {(isWidgetVisible('monthlyWinRate') || isWidgetVisible('dealProbability') || 
                      isWidgetVisible('dealsAtRisk') || isWidgetVisible('timeToClose')) && (
                        <Container header={<Header variant="h2">Performance Analytics</Header>}>
                            <SpaceBetween size="l">
                                {/* Monthly Win Rate Trend */}
                                {isWidgetVisible('monthlyWinRate') && (
                                    <Container header={<Header variant="h3">Monthly Win Rate Trend</Header>}>
                                        {monthlyWinRateData.length > 0 ? (
                                            <MixedLineBarChart
                                                series={[
                                                    {
                                                        title: "Wins",
                                                        type: "bar",
                                                        data: monthlyWinRateData.map(item => ({ 
                                                            x: item.x, 
                                                            y: item.wins 
                                                        })),
                                                        color: "#037f0c" // Green for wins
                                                    },
                                                    {
                                                        title: "Losses", 
                                                        type: "bar",
                                                        data: monthlyWinRateData.map(item => ({ 
                                                            x: item.x, 
                                                            y: item.losses 
                                                        })),
                                                        color: "#d13212" // Red for losses
                                                    },
                                                    {
                                                        title: "Win Rate %",
                                                        type: "line",
                                                        data: monthlyWinRateData.map(item => ({ 
                                                            x: item.x, 
                                                            y: item.winRate 
                                                        })),
                                                        color: "#0972d3" // Blue for win rate line
                                                    }
                                                ]}
                                                ariaLabel="Monthly win rate trend with deal volumes"
                                                height={350}
                                                hideFilter
                                                xScaleType="categorical"
                                                yTitle="Number of Deals / Win Rate (%)"
                                                xTitle="Month"
                                                statusType={chartLoading ? "loading" : "finished"}
                                                empty={
                                                    <Box textAlign="center" color="inherit">
                                                        <b>No data available</b>
                                                        <Box variant="p" color="inherit">
                                                            No closed deals to calculate win rate trend
                                                        </Box>
                                                    </Box>
                                                }
                                            />
                                        ) : (
                                            <Box textAlign="center" padding="l">
                                                <b>No trend data available</b>
                                                <Box variant="p" color="text-body-secondary">
                                                    Need closed deals with dates to show trend
                                                </Box>
                                            </Box>
                                        )}
                                    </Container>
                                )}
                                
                                {/* Deal Probability and Time to Close */}
                                <Grid
                                    gridDefinition={[
                                        { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 6, l: 6, xl: 6 } },
                                        { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 6, l: 6, xl: 6 } }
                                    ]}
                                >
                                    {isWidgetVisible('dealProbability') && (
                                        <Container 
                                            header={
                                                <Header 
                                                    variant="h3"
                                                    info={
                                                        <Popover
                                                            dismissButton={false}
                                                            position="top"
                                                            size="large"
                                                            triggerType="custom"
                                                            content={
                                                                <SpaceBetween size="m">
                                                                    <Box variant="h4">Deal Probability Score Calculation</Box>
                                                                    <Box variant="p">
                                                                        This score is calculated for pending deals based on multiple factors:
                                                                    </Box>
                                                                    <ColumnLayout columns={1}>
                                                                        <SpaceBetween size="xs">
                                                                            <div><strong>Business Qualification:</strong> +20 points if completed</div>
                                                                            <div><strong>Solutions Qualification:</strong> +20 points if completed</div>
                                                                            <div><strong>Existing Cloud Customer:</strong> +15 points if Yes</div>
                                                                            <div><strong>AWS Sentiment:</strong> +15 points if positive</div>
                                                                            <div><strong>Compelling Event:</strong> +10 points if exists</div>
                                                                            <div><strong>Strategic Objective:</strong> +10 points if strategic vs tactical</div>
                                                                            <div><strong>Partner Involvement:</strong> +10 points if partner identified</div>
                                                                        </SpaceBetween>
                                                                    </ColumnLayout>
                                                                    <Box variant="p">
                                                                        <strong>Score Ranges:</strong>
                                                                    </Box>
                                                                    <ColumnLayout columns={1}>
                                                                        <SpaceBetween size="xs">
                                                                            <div>🟢 <strong>High (80-100%):</strong> Very likely to win</div>
                                                                            <div>🟡 <strong>Medium-High (60-79%):</strong> Good chance of winning</div>
                                                                            <div>🟠 <strong>Medium (40-59%):</strong> Moderate chance</div>
                                                                            <div>🔴 <strong>Low (20-39%):</strong> Needs attention</div>
                                                                            <div>⚫ <strong>Very Low (0-19%):</strong> High risk of loss</div>
                                                                        </SpaceBetween>
                                                                    </ColumnLayout>
                                                                </SpaceBetween>
                                                            }
                                                        >
                                                            <Icon name="status-info" />
                                                        </Popover>
                                                    }
                                                >
                                                    Deal Probability Score
                                                </Header>
                                            }
                                        >
                                            <Chart
                                                data={dealProbabilityData}
                                                chartLabel="Deal Probability Score"
                                                chartDescription="Probability distribution of pending deals"
                                                innerMetricDescription="Pending Deals"
                                                innerMetricValue={String(dealProbabilityData.reduce((sum, item) => sum + item.value, 0))}
                                                size="large"
                                                variant="pie"
                                                statusType={chartLoading}
                                            />
                                        </Container>
                                    )}
                                    {isWidgetVisible('timeToClose') && (
                                        <Container header={<Header variant="h3">Time to Close Analysis</Header>}>
                                            <SpaceBetween size="m">
                                                <Box textAlign="center" padding={{ top: 'm', bottom: 'm' }}>
                                                    <Box variant="h3" padding={{ bottom: 's' }}>Average Time to Close</Box>
                                                    <Box variant="h1" color="text-status-info" fontSize="display-l">
                                                        {averageTimeToClose} days
                                                    </Box>
                                                </Box>
                                                {timeToCloseData.length > 0 && (
                                                    <Chart
                                                        data={timeToCloseData}
                                                        chartLabel="Time Distribution"
                                                        chartDescription="Distribution of deal closure times"
                                                        innerMetricDescription="Closed Deals"
                                                        innerMetricValue={String(timeToCloseData.reduce((sum, item) => sum + item.value, 0))}
                                                        size="medium"
                                                        variant="donut"
                                                        statusType={chartLoading}
                                                    />
                                                )}
                                            </SpaceBetween>
                                        </Container>
                                    )}
                                </Grid>
                                
                                {/* Deals at Risk */}
                                {isWidgetVisible('dealsAtRisk') && (
                                    <Container 
                                        header={
                                            <Header 
                                                variant="h3"
                                                actions={
                                                    <Badge color={highRiskDealsCount > 5 ? "red" : highRiskDealsCount > 2 ? "blue" : "green"}>
                                                        {highRiskDealsCount} at risk
                                                    </Badge>
                                                }
                                            >
                                                Deals at Risk
                                            </Header>
                                        }
                                    >
                                        {dealsAtRiskData.length > 0 ? (
                                            <SpaceBetween size="s">
                                                {highRiskDealsCount > 5 && (
                                                    <Alert type="error" header="High Risk Alert">
                                                        You have {highRiskDealsCount} deals at high risk. Immediate attention required.
                                                    </Alert>
                                                )}
                                                {highRiskDealsCount > 2 && highRiskDealsCount <= 5 && (
                                                    <Alert type="warning" header="Medium Risk Alert">
                                                        You have {highRiskDealsCount} deals that need attention.
                                                    </Alert>
                                                )}
                                                <Cards
                                                    items={dealsAtRiskData.slice(0, 5)}
                                                    cardDefinition={{
                                                        header: item => (
                                                            <SpaceBetween direction="horizontal" size="xs">
                                                                <CDLink href={`${window.location.origin}/AllItems/${item.id}/edit`} fontSize="heading-s">
                                                                    {item.companyName}
                                                                </CDLink>
                                                                <Badge color="red">Risk: {item.riskScore}%</Badge>
                                                            </SpaceBetween>
                                                        ),
                                                        sections: [
                                                            {
                                                                id: "risk-factors",
                                                                header: "Risk Factors",
                                                                content: item => (
                                                                    <SpaceBetween size="xs">
                                                                        <div><b>Age:</b> {item.daysSinceCreated} days</div>
                                                                        <div><b>Risk Factors:</b></div>
                                                                        <ul>
                                                                            {item.riskFactors.map((factor: string, index: number) => (
                                                                                <li key={index}>{factor}</li>
                                                                            ))}
                                                                        </ul>
                                                                    </SpaceBetween>
                                                                )
                                                            }
                                                        ]
                                                    }}
                                                    cardsPerRow={[
                                                        { cards: 1 },
                                                        { minWidth: 500, cards: 2 }
                                                    ]}
                                                />
                                            </SpaceBetween>
                                        ) : (
                                            <Box textAlign="center" padding="l">
                                                <Box variant="h3" color="text-status-success">✓ No High-Risk Deals</Box>
                                                <Box variant="p" color="text-body-secondary">
                                                    All pending deals are within acceptable risk parameters
                                                </Box>
                                            </Box>
                                        )}
                                    </Container>
                                )}
                            </SpaceBetween>
                        </Container>
                    )}

                    {/* Items table */}
                    {isWidgetVisible('itemsTable') && (
                        <Container header={<Header variant="h2">Items ({filteredData.length})</Header>}>
                            <Cards
                                items={showAllItems ? filteredData : filteredData.slice(0, 10)}
                                cardDefinition={{
                                    header: item => {
                                        // Calculate probability score for pending deals
                                        const calculateProbabilityScore = (item: any) => {
                                            if (item.tenderOutcome !== 'Pending') return null;
                                            
                                            let score = 0;
                                            if (item.postQualificationBusiness === 'Completed') score += 20;
                                            if (item.postQualificationSolution === 'Completed') score += 20;
                                            if (item.cloudCustomer === 'Yes') score += 15;
                                            if (item.awsSentiment === 'Yes') score += 15;
                                            if (item.compellingEvent === 'Yes') score += 10;
                                            if (item.objective === 'Strategic') score += 10;
                                            if (item.partner && item.partner !== '') score += 10;
                                            
                                            return score;
                                        };

                                        // Calculate risk score for pending deals
                                        const calculateRiskScore = (item: any) => {
                                            if (item.tenderOutcome !== 'Pending') return null;
                                            
                                            const now = Date.now();
                                            const createdDate = parseInt(item.createdAt);
                                            const daysSinceCreated = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
                                            
                                            let riskScore = 0;
                                            if (daysSinceCreated > 90) riskScore += 30;
                                            else if (daysSinceCreated > 60) riskScore += 15;
                                            if (item.postQualificationBusiness !== 'Completed') riskScore += 20;
                                            if (item.postQualificationSolution !== 'Completed') riskScore += 20;
                                            if (item.cloudCustomer === 'No') riskScore += 15;
                                            if (item.awsSentiment === 'No') riskScore += 25;
                                            
                                            return riskScore;
                                        };

                                        const probabilityScore = calculateProbabilityScore(item);
                                        const riskScore = calculateRiskScore(item);

                                        return (
                                            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                                                <CDLink href={`${window.location.origin}/AllItems/${item.id}/edit`} fontSize="heading-m">
                                                    {item.companyName}
                                                </CDLink>
                                                {item.tenderOutcome === 'Pending' && (
                                                    <SpaceBetween direction="horizontal" size="xs">
                                                        {probabilityScore !== null && (
                                                            <Badge 
                                                                color={
                                                                    probabilityScore >= 80 ? "green" :
                                                                    probabilityScore >= 60 ? "blue" :
                                                                    probabilityScore >= 40 ? "grey" :
                                                                    probabilityScore >= 20 ? "red" : "red"
                                                                }
                                                            >
                                                                Win: {probabilityScore}%
                                                            </Badge>
                                                        )}
                                                        {riskScore !== null && riskScore >= 40 && (
                                                            <Badge color="red">
                                                                Risk: {riskScore}%
                                                            </Badge>
                                                        )}
                                                    </SpaceBetween>
                                                )}
                                            </SpaceBetween>
                                        );
                                    },
                                    sections: [
                                        {
                                            id: "description",
                                            header: "Details",
                                            content: item => (
                                                <ColumnLayout columns={2} variant="text-grid">
                                                    <SpaceBetween size="xs">
                                                        <div><b>Contact:</b> {item.primaryBusinessContactName}</div>
                                                        <div><b>Industry:</b> {item.companyIndustry}</div>
                                                        <div><b>Request Type:</b> {item.requestType}</div>
                                                    </SpaceBetween>
                                                    <SpaceBetween size="xs">
                                                        <div><b>Status:</b> {item.qualificationState?.value}</div>
                                                        <div><b>Outcome:</b> {item.tenderOutcome}</div>
                                                        <div><b>Build/Buy:</b> {item.buildVsBuy}</div>
                                                    </SpaceBetween>
                                                </ColumnLayout>
                                            )
                                        }
                                    ]
                                }}
                                cardsPerRow={[
                                    { cards: 1 },
                                    { minWidth: 500, cards: 2 }
                                ]}
                                empty={
                                    <Box textAlign="center" color="inherit">
                                        <b>No items</b>
                                        <Box variant="p" color="inherit">
                                            No items match the current filters
                                        </Box>
                                    </Box>
                                }
                            />
                            {filteredData.length > 10 && (
                                <Box textAlign="center" margin={{ top: "s" }}>
                                    <Button 
                                        onClick={() => setShowAllItems(!showAllItems)}
                                        iconName={showAllItems ? "angle-up" : "angle-down"}
                                    >
                                        {showAllItems 
                                            ? "Show less" 
                                            : `View all ${filteredData.length} items`
                                        }
                                    </Button>
                                </Box>
                            )}
                        </Container>
                    )}
                </SpaceBetween>
            </Container>
            
            {/* Widget Configuration Modal */}
            <Modal
                visible={configModalVisible}
                onDismiss={() => setConfigModalVisible(false)}
                header="Configure Dashboard Widgets"
                size="medium"
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => setConfigModalVisible(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={() => setConfigModalVisible(false)}>
                                Apply
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                <SpaceBetween size="l">
                    <Box>
                        Select which widgets to display on your dashboard:
                    </Box>
                    
                    {/* Group widgets by category */}
                    {['Core Metrics', 'Performance Analytics', 'Financial Metrics', 'Business Metrics', 'Technical Metrics', 'Tables'].map(category => {
                        const categoryWidgets = widgetConfigs.filter(w => w.category === category);
                        if (categoryWidgets.length === 0) return null;
                        
                        return (
                            <Container 
                                key={category}
                                header={<Header variant="h3">{category}</Header>}
                            >
                                <ColumnLayout columns={2}>
                                    {categoryWidgets.map(widget => (
                                        <Toggle
                                            key={widget.id}
                                            checked={widget.visible}
                                            onChange={({ detail }) => handleWidgetToggle(widget.id, detail.checked)}
                                        >
                                            {widget.title}
                                        </Toggle>
                                    ))}
                                </ColumnLayout>
                            </Container>
                        );
                    })}
                </SpaceBetween>
            </Modal>
        </SpaceBetween>
    );
}